import { useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Info,
  Lock,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { rotuloTipo } from "@/lib/tipoProfissional";
import { trpc } from "@/lib/trpc";
import { formatBRL, nomeMes } from "@/lib/utils";
import { PagamentoTurnos } from "@/components/PagamentoTurnos";

// ============================================================
// R4 — Pagamentos
// ============================================================
// Um lugar só para mexer no dinheiro dos turnos. Antes isto vivia escondido
// dentro de uma linha expandida da tela de Comissões; agora Comissões volta a
// ser o que o nome diz (o que a clínica apurou de comissão) e o pagamento tem
// tela própria, com filtro por setor e totais no rodapé.
//
// O que esta tela NÃO faz: calcular. Cada número aqui nasceu em outro lugar —
// o apurado vem congelado da conferência (F2), o pago vem do contas a pagar do
// ERP (F3). A tela junta e mostra; quem decide e grava é a clínica.
//
// Enquanto a chave `escalas_pagamentos_ativo` não for ligada pelo Bryan no
// ERP, todo botão que move dinheiro fica desabilitado e o banner explica por
// quê. Ver o que já existe continua liberado — é o que permite conferir o mês
// antes de ligar a chave.

const SETORES: Array<{ valor: string; rotulo: string }> = [
  { valor: "", rotulo: "Todos os setores" },
  { valor: "clinica", rotulo: "Clínica" },
  { valor: "agro", rotulo: "Agro" },
  { valor: "bt", rotulo: "Banho & Tosa" },
  { valor: "sem_setor", rotulo: "Sem setor definido" },
];

const ROTULO_SETOR: Record<string, string> = {
  clinica: "clínica",
  agro: "agro",
  bt: "banho & tosa",
};

const ESTILO_FECHAMENTO: Record<string, string> = {
  aberto: "bg-amber-100 text-amber-800",
  parcial: "bg-sky-100 text-sky-800",
  pago: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-slate-200 text-slate-600",
};

function reais(centavos: number | null | undefined): string {
  return formatBRL((centavos ?? 0) / 100);
}

export function Pagamentos() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);
  const [setor, setSetor] = useState("");
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [incluirSemTurnos, setIncluirSemTurnos] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);

  const q = trpc.pagamentos.visaoMensal.useQuery({
    ano,
    mes,
    setor: setor ? (setor as "clinica" | "agro" | "bt" | "sem_setor") : null,
    incluirInativos,
    incluirSemTurnos,
  });

  // A liberação vem da sonda própria (`pagamentos.estado`, rota
  // /pagamentos/estado do ERP): ela não depende de existir alguém vinculado
  // nesta lista nem do mês escolhido — a chave é global. Enquanto ela não
  // responder, o banner de "não liberado" não aparece por engano.
  const estado = trpc.pagamentos.estado.useQuery(undefined, { staleTime: 60_000 });

  const itens = q.data?.itens ?? [];
  const totais = q.data?.totais;
  const liberado = estado.data?.liberado ?? null;
  const avisoLiberacao = estado.data?.aviso ?? (estado.error ? estado.error.message : null);

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) {
      novo = 12;
      novoAno--;
    }
    if (novo > 12) {
      novo = 1;
      novoAno++;
    }
    setMes(novo);
    setAno(novoAno);
    setAberto(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pagamentos</h1>
          <p className="text-sm text-slate-600">
            Fechamento da competência, pagamento e recibo — a clínica continua sendo a dona do
            dinheiro.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
          {nomeMes(mes)} {ano}
        </div>
        <Button variant="outline" size="icon" onClick={() => navegar(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span className="text-slate-500">Setor</span>
          <select
            className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
            value={setor}
            onChange={(e) => {
              setSetor(e.target.value);
              setAberto(null);
            }}
          >
            {SETORES.map((s) => (
              <option key={s.valor} value={s.valor}>
                {s.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={incluirSemTurnos}
            onChange={(e) => setIncluirSemTurnos(e.target.checked)}
          />
          Mostrar quem não teve turno
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={incluirInativos}
            onChange={(e) => setIncluirInativos(e.target.checked)}
          />
          Mostrar inativos
        </label>
      </div>

      {liberado === false && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Pagamentos ainda não liberados pelo Bryan.</strong> A chave está desligada no
            ERP da clínica: dá para conferir tudo o que já existe, mas fechar competência, registrar
            pagamento, vincular conta e estornar ficam bloqueados até ela ser ligada lá.
          </span>
        </div>
      )}

      {liberado === null && !estado.isLoading && avisoLiberacao && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Não deu para confirmar com a clínica se os pagamentos estão liberados: {avisoLiberacao}
          </span>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          A ordem é sempre a mesma: <strong>conferir</strong> o mês com a pessoa (em Conferência),
          <strong> fechar</strong> a competência (cria a conta a pagar na clínica) e só então{" "}
          <strong>pagar</strong> — parcial pode. As colunas Fechamento, Pago e Em aberto são o
          último retrato que a clínica devolveu; abrir a linha consulta o ERP de novo e atualiza o
          que estiver na tela.
        </span>
      </div>

      {q.error && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-700">Não deu para montar a lista do mês</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <p>{q.error.message}</p>
            <Button variant="outline" onClick={() => q.refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Tentar de novo
            </Button>
          </CardContent>
        </Card>
      )}

      {q.isLoading && <div className="py-10 text-center text-slate-500">Carregando competência…</div>}

      {!q.isLoading && !q.error && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Apurado no mês" value={reais(totais?.apuradoCentavos)} />
            <Stat label="Já fechado na clínica" value={reais(totais?.fechadoCentavos)} />
            <Stat label="Pago" value={reais(totais?.pagoCentavos)} />
            <Stat label="Em aberto" value={reais(totais?.saldoCentavos)} destaque />
          </div>

          {/* Desktop: tabela. Mobile: cards empilhados, mais abaixo. */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <div className="relative w-full overflow-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3">Colaborador</th>
                      <th className="px-4 py-3">Conferência</th>
                      <th className="px-4 py-3 text-right">Apurado</th>
                      <th className="px-4 py-3">Fechamento</th>
                      <th className="px-4 py-3 text-right">Pago</th>
                      <th className="px-4 py-3 text-right">Em aberto</th>
                      <th className="w-10 px-2 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((i) => (
                      <Linha
                        key={i.profissionalId}
                        item={i}
                        ano={ano}
                        mes={mes}
                        aberto={aberto === i.profissionalId}
                        onToggle={() =>
                          setAberto(aberto === i.profissionalId ? null : i.profissionalId)
                        }
                      />
                    ))}
                    {!itens.length && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                          Ninguém nesta competência com os filtros escolhidos.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {itens.length > 0 && (
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                      <tr className="font-semibold text-slate-800">
                        <td className="px-4 py-3">
                          Totais · {totais?.pessoas ?? 0} pessoa(s)
                        </td>
                        <td className="px-4 py-3 text-xs font-normal text-slate-500">
                          {totais?.conferidos ?? 0} conferida(s)
                        </td>
                        <td className="px-4 py-3 text-right">{reais(totais?.apuradoCentavos)}</td>
                        <td className="px-4 py-3 text-xs font-normal text-slate-500">
                          {totais?.comFechamento ?? 0} fechada(s) · {reais(totais?.fechadoCentavos)}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-700">
                          {reais(totais?.pagoCentavos)}
                        </td>
                        <td className="px-4 py-3 text-right">{reais(totais?.saldoCentavos)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3 md:hidden">
            {itens.map((i) => (
              <CardLinha
                key={i.profissionalId}
                item={i}
                ano={ano}
                mes={mes}
                aberto={aberto === i.profissionalId}
                onToggle={() => setAberto(aberto === i.profissionalId ? null : i.profissionalId)}
              />
            ))}
            {itens.length > 0 && (
              <Card>
                <CardContent className="space-y-1 p-4 text-sm">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Totais do mês
                  </div>
                  <Par rotulo="Pessoas" valor={String(totais?.pessoas ?? 0)} />
                  <Par rotulo="Conferências fechadas" valor={String(totais?.conferidos ?? 0)} />
                  <Par rotulo="Apurado" valor={reais(totais?.apuradoCentavos)} />
                  <Par rotulo="Fechado na clínica" valor={reais(totais?.fechadoCentavos)} />
                  <Par rotulo="Pago" valor={reais(totais?.pagoCentavos)} />
                  <Par rotulo="Em aberto" valor={reais(totais?.saldoCentavos)} />
                </CardContent>
              </Card>
            )}
          </div>

          {(totais?.semVinculo ?? 0) > 0 && (
            <p className="text-xs text-slate-500">
              {totais?.semVinculo} pessoa(s) desta lista ainda não têm vínculo com a ficha da
              clínica. Sem vínculo não existe conta a pagar — resolva em Configurações › Vínculos.
            </p>
          )}
        </>
      )}
    </div>
  );
}

type Item = {
  profissionalId: number;
  nome: string;
  tipo: string;
  ativo: boolean;
  setor: string | null;
  semVinculo: boolean;
  turnos: {
    qtd: number;
    realizados: number;
    pendentes: number;
    previstoCentavos: number;
    apuradoCentavos: number;
  };
  conferencia: {
    status: string;
    totalApuradoCentavos: number;
    confirmadoPeloProfissionalEm: Date | string | null;
  } | null;
  fechamento: {
    id: number;
    status: string;
    apuradoCentavos: number;
    pagoCentavos: number;
    saldoCentavos: number;
  } | null;
};

function Identificacao({ item }: { item: Item }) {
  return (
    <>
      <div className={item.semVinculo ? "font-medium text-red-700" : "font-medium text-slate-900"}>
        {item.nome}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        <Badge variant="outline">{rotuloTipo(item.tipo)}</Badge>
        {item.setor ? (
          <Badge variant="outline">{ROTULO_SETOR[item.setor] ?? item.setor}</Badge>
        ) : (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500">sem setor</span>
        )}
        {!item.ativo && <Badge variant="inativo">inativo</Badge>}
        {item.semVinculo && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            sem vínculo com a clínica
          </span>
        )}
      </div>
    </>
  );
}

function SeloConferencia({ item }: { item: Item }) {
  const conferido = item.conferencia?.status === "conferido";
  return (
    <div className="space-y-1">
      <span
        className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
          conferido ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
        }`}
      >
        {conferido ? "conferida" : (item.conferencia?.status ?? "não aberta")}
      </span>
      {item.turnos.pendentes > 0 && (
        <div className="text-xs text-amber-700">{item.turnos.pendentes} turno(s) sem marcação</div>
      )}
      {item.conferencia?.confirmadoPeloProfissionalEm && (
        <div className="text-xs text-slate-500">confirmada pelo colaborador</div>
      )}
    </div>
  );
}

function Linha({
  item,
  ano,
  mes,
  aberto,
  onToggle,
}: {
  item: Item;
  ano: number;
  mes: number;
  aberto: boolean;
  onToggle: () => void;
}) {
  const f = item.fechamento;
  return (
    <>
      <tr
        className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${
          item.semVinculo ? "bg-red-50/60" : ""
        }`}
        onClick={onToggle}
      >
        <td className="px-4 py-3 align-top">
          <Identificacao item={item} />
        </td>
        <td className="px-4 py-3 align-top">
          <SeloConferencia item={item} />
        </td>
        <td className="px-4 py-3 text-right align-top font-medium text-slate-900">
          {reais(item.conferencia?.status === "conferido"
            ? item.conferencia.totalApuradoCentavos
            : item.turnos.apuradoCentavos)}
          <div className="text-xs font-normal text-slate-400">
            {item.turnos.realizados}/{item.turnos.qtd} turno(s)
          </div>
        </td>
        <td className="px-4 py-3 align-top">
          {f ? (
            <span
              className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                ESTILO_FECHAMENTO[f.status] ?? "bg-slate-200 text-slate-600"
              }`}
            >
              #{f.id} · {f.status}
            </span>
          ) : (
            <span className="text-xs text-slate-400">sem fechamento</span>
          )}
        </td>
        <td className="px-4 py-3 text-right align-top text-emerald-700">
          {f ? reais(f.pagoCentavos) : "—"}
        </td>
        <td className="px-4 py-3 text-right align-top text-slate-900">
          {f ? reais(f.saldoCentavos) : "—"}
        </td>
        <td className="px-2 py-3 text-right align-top text-slate-400">
          {aberto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {aberto && (
        <tr className="border-b border-slate-100 bg-slate-50/60">
          <td colSpan={7} className="px-4 py-4">
            <PagamentoTurnos
              profissionalId={item.profissionalId}
              nome={item.nome}
              ano={ano}
              mes={mes}
              semVinculo={item.semVinculo}
              conferencia={item.conferencia}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function CardLinha({
  item,
  ano,
  mes,
  aberto,
  onToggle,
}: {
  item: Item;
  ano: number;
  mes: number;
  aberto: boolean;
  onToggle: () => void;
}) {
  const f = item.fechamento;
  return (
    <Card className={item.semVinculo ? "border-red-200" : ""}>
      <CardContent className="p-4">
        <button
          className="flex w-full items-start justify-between gap-2 text-left"
          onClick={onToggle}
        >
          <div>
            <Identificacao item={item} />
          </div>
          <span className="mt-1 text-slate-400">
            {aberto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>

        <div className="mt-3 space-y-1 text-sm">
          <Par
            rotulo="Conferência"
            valor={item.conferencia?.status === "conferido" ? "conferida" : (item.conferencia?.status ?? "não aberta")}
          />
          <Par
            rotulo="Apurado"
            valor={reais(
              item.conferencia?.status === "conferido"
                ? item.conferencia.totalApuradoCentavos
                : item.turnos.apuradoCentavos,
            )}
          />
          <Par rotulo="Fechamento" valor={f ? `#${f.id} · ${f.status}` : "sem fechamento"} />
          <Par rotulo="Pago" valor={f ? reais(f.pagoCentavos) : "—"} />
          <Par rotulo="Em aberto" valor={f ? reais(f.saldoCentavos) : "—"} />
        </div>

        {aberto && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <PagamentoTurnos
              profissionalId={item.profissionalId}
              nome={item.nome}
              ano={ano}
              mes={mes}
              semVinculo={item.semVinculo}
              conferencia={item.conferencia}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Par({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{rotulo}</span>
      <span className="font-medium text-slate-900">{valor}</span>
    </div>
  );
}

function Stat({ label, value, destaque }: { label: string; value: string; destaque?: boolean }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-bold ${destaque ? "text-brand-600" : "text-slate-900"}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
